
import './Nudge.scss';

import React, { forwardRef, useCallback, useEffect, useState } from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';

import ActionGroups from '@ulta/core/components/ActionGroups/ActionGroups';
import Button from '@ulta/core/components/Button/Button';
import Icon from '@ulta/core/components/Icon/Icon';
import Link_Huge from '@ulta/core/components/Link_Huge/Link_Huge';
import Markdown from '@ulta/core/components/Markdown/Markdown';
import Text from '@ulta/core/components/Text/Text';
import { hasItems } from '@ulta/core/utils/array/array';
import { isServer } from '@ulta/core/utils/device_detection/device_detection';
import { handleRedirect } from '@ulta/core/utils/handleLocation/handleLocation';
import { getStorage, setStorage } from '@ulta/core/utils/storage/storage';

import { isAuthButton } from '@ulta/utils/actionUtils/actionUtils';

import * as utils from './Nudge';

/**
 * Represents a Nudge component that prompts users with messages, actions, and benefit icon lists.
 *
 * @method Nudge
 * @param {NudgeProps} props - React properties passed from composition
 * @param {React.Ref} ref - Reference hook passed from parent
 * @returns {JSX.Element|null} The Nudge component or null if not showing
 */
export const Nudge = forwardRef( ( props, ref ) => {
  const [showNudge, setShowNudge] = useState( false );
  const {
    action,
    actionGroups,
    benefitsList,
    closeAction,
    message,
    title,
    type,
    variant = defaultProps.variant
  } = props;

  /** Action and message checks */
  const { actions = [] } = actionGroups || {};
  const hasAction = action && action?.label;
  const isActionGroupsVisible = actionGroups && actions?.[0]?.label && ( variant === 'iconList' );
  const hasMessage = message && message?.body;

  /** Storage, Close and Show handling for the Nudge */
  const handleSetStorage = useCallback( utils.composeHandleSetStorage( { setStorage } ), [setStorage] );
  const handleCloseNudge = useCallback( utils.composeCloseNudge( { setShowNudge, handleSetStorage }, type ), [setShowNudge, handleSetStorage] );
  const handleShowNudge = useCallback( utils.composeShowNudge( { setShowNudge, getStorage, handleSetStorage }, type ), [setShowNudge, getStorage, handleSetStorage] );
  const handleRedirectNudgeAction = useCallback( ( e ) => {
    handleCloseNudge( e );
    handleRedirect( { url: action?.url } );
  }, [handleCloseNudge, action] );

  if( ref !== null ){
    // eslint-disable-next-line no-param-reassign
    ref.current = { handleCloseNudge };
  }

  useEffect( () => {
    handleShowNudge();
  }, [] );

  if( !title || !showNudge || isServer() ){
    return null;
  }

  /** Render the Benefits List */
  const renderBenefits = () => {

    if( !hasItems( benefitsList ) ){
      return null;
    }

    return (
      <ul className='Nudge__benefitsList'>
        { benefitsList.map( ( item, index ) => {
          const subtitleText = item?.subtitle;
          const iconAsset = item?.icon;

          if( !subtitleText || !iconAsset ){
            return null;
          }

          return (
            <li key={ item?.componentKey || `benefit-${index}` } className='Nudge__benefitItem'>
              <Icon
                className='Nudge__benefitIcon'
                name={ iconAsset }
                size='xl'
              />
              <Text
                className='Nudge__benefitText'
                htmlTag='span'
                textStyle='body-lg'
              >
                { subtitleText }
              </Text>
            </li>
          );
        } ) }
      </ul>
    );
  };

  /** Render the default layout body (message & action) */
  const renderDefaultBody = () => {
    const isAuth = hasAction && isAuthButton( action );
    const isStandard = hasAction && !isAuthButton( action );

    return (
      <>
        { hasMessage &&
          <Markdown
            alignment={ message?.alignment }
            bodyStyle='body-3-noPadding'
            cmsMarkdownContent={ message?.body }
          />
        }
        { isAuth &&
          <Link_Huge
            action={ action }
            className='Nudge__actionLink'
          >
            { action?.label }
          </Link_Huge>
        }
        { isStandard &&
          <Button
            likeLink={ true }
            className='Nudge__action'
            action={ action }
            onClick={ action?.navigationType === 'redirect' ? handleRedirectNudgeAction : handleCloseNudge }
            label={ action?.label }
          />
        }
      </>
    );
  };

  /** Render the dynamic body based on the layout variant */
  const renderContentBody = () => {
    if( variant === 'iconList' ){
      return renderBenefits();
    }
    return renderDefaultBody();
  };

  const nudgeClassName = classNames( 'Nudge', {
    Nudge_mfa: type === 'mfa',
    [`Nudge_${variant}`]: variant
  } );

  return (
    <div
      className={ nudgeClassName }
      role='tooltip'
      aria-label='sign in'
    >
      <div className='Nudge__wrapper'>
        <div className='Nudge__arrow'></div>
        <div className='Nudge__topContainer'>
          <div className='Nudge__content'>
            <Text
              htmlTag={ title?.htmlTag }
              textStyle='title-sm'
            >
              { title?.text }
            </Text>
            { renderContentBody() }
          </div>
          <div className='Nudge__close'>
            <Button
              variant='unstyled'
              icon
              ariaLabel={ closeAction?.label }
              iconImage='X'
              action={ closeAction }
              onClick={ handleCloseNudge }
            />
          </div>
        </div>
        { isActionGroupsVisible &&
          <div className='Nudge__actions'>
            <ActionGroups
              actions={ actions }
              style={ actionGroups?.style }
              types={ actionGroups?.types }
              variant={ actionGroups?.variant }
            />
          </div>
        }
      </div>
    </div>
  );
} );

/**
 * Returns a function that closes the nudge and sets storage for 3 days.
 *
 * @param {object} methods - Object containing setShowNudge and handleSetStorage methods
 * @param {function} methods.setShowNudge - Method to set showNudge state
 * @param {function} methods.handleSetStorage - Method to set storage value
 * @param {string} [type] - Nudge type modifier (e.g., 'mfa')
 *
 * @returns {function} - Function that closes the nudge and sets storage
 */
export const composeCloseNudge = ( methods, type ) => () => {
  const { setShowNudge, handleSetStorage } = methods || {};

  if( !setShowNudge || !handleSetStorage ){
    return;
  }

  handleSetStorage();
  setShowNudge( false );
  if( type === 'mfa' ){
    setStorage( { key: 'MFA_Nudge', value: {}, secure: true } ) || {};
  }
};

/**
 * Show nudge if it has expired
 * @function composeShowNudge
 * @param {object} methods - Object containing setShowNudge, getStorage, and handleSetStorage methods
 * @param {function} methods.setShowNudge - Method to set showNudge state
 * @param {function} methods.getStorage - Method to retrieve storage value
 * @param {function} methods.handleSetStorage - Method to set storage value
 * @param {string} [type] - Nudge type modifier (e.g., 'mfa')
 * @returns {function} Function to show nudge if it has expired
 */
export const composeShowNudge = ( methods, type ) => () => {
  const { setShowNudge, getStorage, handleSetStorage } = methods || {};

  if( !setShowNudge || !getStorage || !handleSetStorage ){
    return;
  }
  const isNudgeVisible = !getStorage( { key: type === 'mfa' ? 'MFA_Nudge' : 'Nudge', secure: true } );
  setShowNudge( isNudgeVisible );
  handleSetStorage();
};

/**
 * Closes the nudge and sets storage to expire in 3 days
 * @param {object} methods - Object containing setStorage method
 * @param {function} methods.setStorage - Method to set storage value
 * @returns {function} Function to close nudge and set storage
 */
export const composeHandleSetStorage = ( methods ) => () => {
  const { setStorage } = methods || {};

  if( !setStorage ){
    return;
  }
  const threeDays = 3 * 24 * 60 * 60 * 1000;
  setStorage( { key: 'Nudge', value: {}, ttl: threeDays, secure: true } ) || {};
};

/**
 * Property type definitions
 * @typedef NudgeProps
 * @type {object}
 * @property {object} action - The main CTA config of the nudge.
 * @property {object} actionGroups - Layout grouping config for secondary action flows.
 * @property {Array} benefitsList - Array of custom benefit items displaying icons and subtitles.
 * @property {object} closeAction - The close button action config of the nudge.
 * @property {object} message - The message config of the nudge (Markdown).
 * @property {object} title - The title config of the nudge.
 * @property {string} type - Type modifier for Nudge styling (e.g., 'mfa').
 * @property {string} variant - Specific layout variant modifier (e.g., 'iconList').
 */
export const propTypes = {
  action: PropTypes.object,
  actionGroups: PropTypes.object,
  benefitsList: PropTypes.arrayOf(
    PropTypes.shape( {
      subtitle: PropTypes.oneOfType( [PropTypes.string, PropTypes.object] ),
      icon: PropTypes.string
    } )
  ),
  closeAction: PropTypes.object,
  message: PropTypes.object,
  title: PropTypes.object,
  type: PropTypes.oneOf( ['mfa'] ),
  variant: PropTypes.string
};

/**
 * Property type definitions
 * @type {object}
 * @property {string} variant - set the default variant for Nudge
 */
export const defaultProps = {
  variant: 'default'
};

Nudge.propTypes = propTypes;
Nudge.defaultProps = defaultProps;

Nudge.displayName = 'Nudge';

export default Nudge;
